import { useEffect, useState } from "react";
import { toast } from "sonner";
import { mutate } from "swr";
import { Youtube, Loader2, CheckCircle2, AlertCircle, Download } from "lucide-react";
import {
  getPlaylistImportStatus,
  startPlaylistImport,
  formatNumber,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const STATUS_STYLES = {
  idle: { color: "text-white/50", label: "Ready" },
  running: { color: "text-emerald-400", label: "Importing" },
  done: { color: "text-emerald-400", label: "Complete" },
  error: { color: "text-red-400", label: "Error" },
};

export default function PlaylistImportWidget() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setState(await getPlaylistImportStatus());
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const active = state?.status === "running";
    const t = setInterval(async () => {
      const prev = state?.status;
      await load();
      // When the run just finished, refresh the collections grid
      if (prev === "running") {
        await mutate("/collections");
      }
    }, active ? 1500 : 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.status]);

  const handleStart = async () => {
    setBusy(true);
    try {
      const res = await startPlaylistImport();
      if (res?.ok) {
        toast.success("Import started — pulling playlists from YouTube");
      } else {
        toast.message(res?.message || "Import already running");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start import");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const status = state.status || "idle";
  const meta = STATUS_STYLES[status] || STATUS_STYLES.idle;
  const total = state.total_playlists || 0;
  const done = state.processed_playlists || 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const isRunning = status === "running";
  const Icon =
    status === "running"
      ? Loader2
      : status === "done"
      ? CheckCircle2
      : status === "error"
      ? AlertCircle
      : Youtube;

  return (
    <div
      data-testid="playlist-import-widget"
      className="rounded-2xl border border-white/10 bg-[#12141d] p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label inline-flex items-center gap-2">
            <Youtube className="w-3.5 h-3.5" /> Mirror YouTube playlists
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Icon
              className={`w-4 h-4 ${meta.color} ${isRunning ? "animate-spin" : ""}`}
            />
            <span
              data-testid="import-status-label"
              className={`font-mono-tech text-xs uppercase tracking-[0.2em] ${meta.color}`}
            >
              {meta.label}
            </span>
            {total > 0 && (
              <span className="mono-label text-white/40">
                · {formatNumber(done)} / {formatNumber(total)} playlists
              </span>
            )}
          </div>
        </div>
        {!isRunning && (
          <Button
            onClick={handleStart}
            disabled={busy}
            data-testid="import-playlists-btn"
            className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.2em] px-5"
          >
            <Download className="w-4 h-4 mr-2" />
            {status === "done" ? "Re-import" : status === "error" ? "Retry import" : "Import from YouTube"}
          </Button>
        )}
      </div>

      {(isRunning || total > 0) && (
        <div className="mt-5">
          <div className="flex items-center justify-between mono-label text-white/40">
            <span data-testid="import-current-playlist" className="truncate max-w-[70%]">
              {state.current_playlist ? `▶ ${state.current_playlist}` : state.message || ""}
            </span>
            <span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2 mt-2 bg-white/5 [&>*]:bg-[#d95d39]" />
        </div>
      )}

      {(state.collections_created || state.collections_updated || state.videos_added) ? (
        <div className="mt-5 grid grid-cols-3 gap-px bg-white/10 rounded-xl overflow-hidden">
          {[
            { l: "Created", v: state.collections_created || 0 },
            { l: "Updated", v: state.collections_updated || 0 },
            { l: "New videos", v: state.videos_added || 0 },
          ].map((s) => (
            <div key={s.l} className="bg-[#0c0e15] p-3 text-center">
              <div className="font-serif-editorial text-lg text-[#f4f1ea]">
                {formatNumber(s.v)}
              </div>
              <div className="mono-label text-white/40 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
